import { addZonedDays, startOfZonedDay, zonedYmd } from './reporting/timezone';

/** Danish weekday keys used by clinics.business_hours / online_booking_hours. */
export const DK_WEEKDAY_KEYS = [
  'sondag',
  'mandag',
  'tirsdag',
  'onsdag',
  'torsdag',
  'fredag',
  'lordag',
] as const;

export type DkWeekdayKey = (typeof DK_WEEKDAY_KEYS)[number];

export type ClinicHoursExceptionType = 'closed' | 'custom_hours';
export type ClinicHoursSource = 'weekly' | 'exception';
export type ClinicHoursMode = 'business' | 'online' | 'calendar';

export interface HourPeriod {
  start: string;
  end: string;
}

export interface WeeklyDayHours {
  enabled: boolean;
  start: string;
  end: string;
}

export type WeeklyHoursShape = Partial<Record<DkWeekdayKey, WeeklyDayHours | null>>;

export interface ClinicHoursException {
  id: string;
  clinic_id: string;
  exception_date: string;
  exception_type: ClinicHoursExceptionType;
  periods: HourPeriod[];
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ResolvedClinicDayHours {
  enabled: boolean;
  periods: HourPeriod[];
  source: ClinicHoursSource;
  exceptionId: string | null;
  exceptionType: ClinicHoursExceptionType | null;
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export function assertNever(value: never, message = 'Unhandled union member'): never {
  throw new Error(`${message}: ${String(value)}`);
}

export function dkWeekdayKeyFromDate(date: Date): DkWeekdayKey {
  return DK_WEEKDAY_KEYS[date.getDay()];
}

export function dkWeekdayKeyFromCivilDate(isoDate: string): DkWeekdayKey {
  const parsed = parseCivilDate(isoDate);
  return dkWeekdayKeyFromDate(parsed);
}

export function parseCivilDate(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

export function civilDateFromLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function hhmmToMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return h * 60 + (Number.isFinite(m) ? m : 0);
}

export function isValidHhmm(value: string): boolean {
  return HHMM.test(value);
}

export function normalizeHourPeriods(periods: HourPeriod[]): HourPeriod[] {
  const normalized = periods.map((period) => ({
    start: period.start,
    end: period.end,
  }));
  normalized.sort((a, b) => hhmmToMinutes(a.start) - hhmmToMinutes(b.start));
  let prevEnd = -1;
  for (const period of normalized) {
    if (!isValidHhmm(period.start) || !isValidHhmm(period.end)) {
      throw new Error('invalid periods: start/end must be HH:MM');
    }
    const startMin = hhmmToMinutes(period.start);
    const endMin = hhmmToMinutes(period.end);
    if (endMin <= startMin) {
      throw new Error('invalid periods: end must be after start');
    }
    if (startMin < prevEnd) {
      throw new Error('invalid periods: windows must not overlap');
    }
    prevEnd = endMin;
  }
  return normalized;
}

export function parseWeeklyHours(value: unknown): WeeklyHoursShape {
  const out: WeeklyHoursShape = {};
  if (!value || typeof value !== 'object' || Array.isArray(value)) return out;
  const obj = value as Record<string, unknown>;
  for (const key of DK_WEEKDAY_KEYS) {
    const day = obj[key];
    if (!day || typeof day !== 'object' || Array.isArray(day)) continue;
    const rec = day as Record<string, unknown>;
    out[key] = {
      enabled: Boolean(rec.enabled),
      start: typeof rec.start === 'string' ? rec.start : '08:00',
      end: typeof rec.end === 'string' ? rec.end : '16:00',
    };
  }
  return out;
}

export function weeklyHoursByJsDay(weekly: WeeklyHoursShape): Map<number, WeeklyDayHours | null> {
  const map = new Map<number, WeeklyDayHours | null>();
  DK_WEEKDAY_KEYS.forEach((key, dayOfWeek) => {
    map.set(dayOfWeek, weekly[key] ?? null);
  });
  return map;
}

export function weeklyHoursAnyEnabled(weekly: WeeklyHoursShape): boolean {
  return DK_WEEKDAY_KEYS.some((key) => weekly[key]?.enabled);
}

export function calendarWeeklyHours(
  businessHours: unknown,
  onlineBookingHours: unknown,
): WeeklyHoursShape {
  const online = parseWeeklyHours(onlineBookingHours);
  if (weeklyHoursAnyEnabled(online)) return online;
  return parseWeeklyHours(businessHours);
}

export function weeklyDayToResolved(
  weekly: WeeklyDayHours | null | undefined,
): Pick<ResolvedClinicDayHours, 'enabled' | 'periods'> {
  if (!weekly?.enabled) return { enabled: false, periods: [] };
  if (
    !isValidHhmm(weekly.start) ||
    !isValidHhmm(weekly.end) ||
    hhmmToMinutes(weekly.end) <= hhmmToMinutes(weekly.start)
  ) {
    return { enabled: false, periods: [] };
  }
  return {
    enabled: true,
    periods: [{ start: weekly.start, end: weekly.end }],
  };
}

export function resolveClinicDayHours(input: {
  weekly: WeeklyDayHours | null | undefined;
  exception?: ClinicHoursException | null;
}): ResolvedClinicDayHours {
  const weekly = weeklyDayToResolved(input.weekly);
  const exception = input.exception;
  if (!exception) {
    return {
      ...weekly,
      source: 'weekly',
      exceptionId: null,
      exceptionType: null,
    };
  }

  switch (exception.exception_type) {
    case 'closed':
      return {
        enabled: false,
        periods: [],
        source: 'exception',
        exceptionId: exception.id,
        exceptionType: 'closed',
      };
    case 'custom_hours': {
      let periods: HourPeriod[] = [];
      try {
        periods = normalizeHourPeriods(exception.periods ?? []);
      } catch {
        periods = [];
      }
      return {
        enabled: periods.length > 0,
        periods,
        source: 'exception',
        exceptionId: exception.id,
        exceptionType: 'custom_hours',
      };
    }
    default:
      return assertNever(exception.exception_type);
  }
}

export function resolveClinicHoursForCivilDate(input: {
  isoDate: string;
  weeklyHours: WeeklyHoursShape;
  exception?: ClinicHoursException | null;
}): ResolvedClinicDayHours {
  const key = dkWeekdayKeyFromCivilDate(input.isoDate);
  return resolveClinicDayHours({
    weekly: input.weeklyHours[key],
    exception: input.exception ?? null,
  });
}

export function resolveClinicHoursForLocalDate(input: {
  date: Date;
  weeklyHours: WeeklyHoursShape;
  exception?: ClinicHoursException | null;
}): ResolvedClinicDayHours {
  return resolveClinicHoursForCivilDate({
    isoDate: civilDateFromLocalDate(input.date),
    weeklyHours: input.weeklyHours,
    exception: input.exception,
  });
}

export function exceptionByDate(
  exceptions: ClinicHoursException[] | undefined,
): Map<string, ClinicHoursException> {
  const map = new Map<string, ClinicHoursException>();
  for (const exception of exceptions ?? []) {
    map.set(exception.exception_date, exception);
  }
  return map;
}

export function formatHourPeriods(periods: HourPeriod[]): string {
  return periods.map((period) => `${period.start}–${period.end}`).join(', ');
}

export type ClinicDayHoursKind =
  | 'normal_open'
  | 'normal_closed'
  | 'exception_open'
  | 'exception_closed'
  | 'exception_custom';

export function clinicDayHoursKind(
  weekly: WeeklyDayHours | null | undefined,
  resolved: ResolvedClinicDayHours,
): ClinicDayHoursKind {
  if (resolved.source === 'weekly') {
    return resolved.enabled ? 'normal_open' : 'normal_closed';
  }
  switch (resolved.exceptionType) {
    case 'closed':
      return 'exception_closed';
    case 'custom_hours':
      return weekly?.enabled ? 'exception_custom' : 'exception_open';
    case null:
      return weekly?.enabled ? 'normal_open' : 'normal_closed';
    default:
      return assertNever(resolved.exceptionType);
  }
}

/** True when a date-specific exception closes the clinic. Weekly closed days are not this. */
export function isClosedHoursException(resolved: ResolvedClinicDayHours): boolean {
  return resolved.source === 'exception' && resolved.exceptionType === 'closed';
}

export function intervalFitsWindows(
  windows: HourPeriod[],
  startMinutes: number,
  endMinutes: number,
): boolean {
  if (endMinutes <= startMinutes) return false;
  return windows.some((period) => {
    return startMinutes >= hhmmToMinutes(period.start) && endMinutes <= hhmmToMinutes(period.end);
  });
}

export function intervalFitsResolvedHours(
  resolved: ResolvedClinicDayHours,
  startMinutes: number,
  endMinutes: number,
): boolean {
  if (!resolved.enabled) return false;
  return intervalFitsWindows(resolved.periods, startMinutes, endMinutes);
}

export function localIntervalFitsResolvedHours(
  resolved: ResolvedClinicDayHours,
  start: Date,
  end: Date,
): boolean {
  if (civilDateFromLocalDate(start) !== civilDateFromLocalDate(end)) return false;
  const startMin = start.getHours() * 60 + start.getMinutes();
  const endMin = end.getHours() * 60 + end.getMinutes() + (end.getSeconds() > 0 ? 1 : 0);
  return intervalFitsResolvedHours(resolved, startMin, endMin);
}

export function isCivilDateOpen(resolved: ResolvedClinicDayHours): boolean {
  return resolved.enabled && resolved.periods.length > 0;
}

export function resolvedToCalendarDayHours(resolved: ResolvedClinicDayHours): {
  enabled: boolean;
  start: string;
  end: string;
  periods: HourPeriod[];
  source: ClinicHoursSource;
  exceptionType: ClinicHoursExceptionType | null;
} {
  return {
    enabled: resolved.enabled,
    start: resolved.periods[0]?.start ?? '08:00',
    end: resolved.periods[resolved.periods.length - 1]?.end ?? '16:00',
    periods: resolved.periods,
    source: resolved.source,
    exceptionType: resolved.exceptionType,
  };
}

export function defaultOpenPeriods(weekly: WeeklyHoursShape): HourPeriod[] {
  for (const key of DK_WEEKDAY_KEYS) {
    const day = weekly[key];
    if (day?.enabled && isValidHhmm(day.start) && isValidHhmm(day.end) && hhmmToMinutes(day.end) > hhmmToMinutes(day.start)) {
      return [{ start: day.start, end: day.end }];
    }
  }
  return [{ start: '09:00', end: '15:00' }];
}

export function exceptionFromPublic(row: {
  date: string;
  exception_type: ClinicHoursExceptionType;
  periods: HourPeriod[];
}): ClinicHoursException {
  return {
    id: `public-${row.date}`,
    clinic_id: '',
    exception_date: row.date,
    exception_type: row.exception_type,
    periods: parseExceptionPeriods(row.periods),
  };
}

export function parseExceptionPeriods(value: unknown): HourPeriod[] {
  if (!Array.isArray(value)) return [];
  const periods: HourPeriod[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const rec = item as Record<string, unknown>;
    if (typeof rec.start === 'string' && typeof rec.end === 'string') {
      periods.push({ start: rec.start, end: rec.end });
    }
  }
  return periods;
}

export function intersectPeriods(clinic: HourPeriod[], practitioner: HourPeriod[]): HourPeriod[] {
  const result: HourPeriod[] = [];
  for (const a of clinic) {
    for (const b of practitioner) {
      const start = hhmmToMinutes(a.start) >= hhmmToMinutes(b.start) ? a.start : b.start;
      const end = hhmmToMinutes(a.end) <= hhmmToMinutes(b.end) ? a.end : b.end;
      if (hhmmToMinutes(end) > hhmmToMinutes(start)) {
        result.push({ start, end });
      }
    }
  }
  return result;
}

/** HH:MM windows whose end is after the start. End-before-start and bad clocks are dropped. */
export function usableHourPeriods(periods: HourPeriod[]): HourPeriod[] {
  const usable: HourPeriod[] = [];
  for (const period of periods) {
    if (!isValidHhmm(period.start) || !isValidHhmm(period.end)) continue;
    if (hhmmToMinutes(period.end) <= hhmmToMinutes(period.start)) continue;
    usable.push({ start: period.start, end: period.end });
  }
  return usable;
}

/**
 * Grey-out windows for one practitioner on the Aftaler calendar.
 * Valid clinic hours are intersected with that practitioner's windows.
 * A weekly-closed day, or clinic clocks that are not a real range, falls
 * back to the practitioner's own hours. A dated clinic-wide closed exception
 * still yields no windows. Online booking uses getEffectiveBookableWindows
 * and does not clip weekly clinic hours; this fallback keeps the painted
 * open window inside those bookable hours.
 */
export function combineClinicAndPractitionerPeriods(
  clinic: Pick<ResolvedClinicDayHours, 'enabled' | 'periods' | 'source' | 'exceptionType'>,
  practitionerPeriods: HourPeriod[],
): HourPeriod[] {
  if (clinic.source === 'exception' && clinic.exceptionType === 'closed') return [];
  const clinicPeriods = clinic.enabled ? usableHourPeriods(clinic.periods) : [];
  if (clinicPeriods.length === 0) return usableHourPeriods(practitionerPeriods);
  return intersectPeriods(clinicPeriods, practitionerPeriods);
}

function localIntervalFitsHourPeriods(periods: HourPeriod[], start: Date, end: Date): boolean {
  if (civilDateFromLocalDate(start) !== civilDateFromLocalDate(end)) return false;
  const startMin = start.getHours() * 60 + start.getMinutes();
  const endMin = end.getHours() * 60 + end.getMinutes() + (end.getSeconds() > 0 ? 1 : 0);
  return intervalFitsWindows(periods, startMin, endMin);
}

/**
 * Warning flag for a time on the practitioner calendar.
 * A slot inside the painted window is not outside hours. That window is the
 * clinic ∩ practitioner intersection, or the practitioner's own hours when
 * the clinic weekday is closed or invalid. Anything else keeps the clinic-hours
 * check. A dated closed exception fits neither, so it still warns; callers
 * block that date separately.
 */
export function outsideShownCalendarHours(
  resolved: ResolvedClinicDayHours,
  practitionerPeriods: HourPeriod[],
  start: Date,
  end: Date,
): boolean {
  const shown = combineClinicAndPractitionerPeriods(resolved, practitionerPeriods);
  if (localIntervalFitsHourPeriods(shown, start, end)) return false;
  return !localIntervalFitsResolvedHours(resolved, start, end);
}

export interface ClinicianDayHours {
  enabled?: boolean;
  start?: string;
  end?: string;
}

export type MemberExceptionType =
  | 'unavailable'
  | 'custom_hours'
  | 'meeting'
  | 'vacation'
  | 'sick';

export interface MemberDayException {
  type: MemberExceptionType;
  startsTime?: string | null;
  endsTime?: string | null;
}

function isBlockingMemberException(type: MemberExceptionType): boolean {
  switch (type) {
    case 'unavailable':
    case 'vacation':
    case 'sick':
    case 'meeting':
      return true;
    case 'custom_hours':
      return false;
    default: {
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

export function applyMemberDayExceptions(
  weeklyPeriods: HourPeriod[],
  exceptions: readonly MemberDayException[],
): HourPeriod[] {
  if (exceptions.some((item) => isBlockingMemberException(item.type) && !item.startsTime && !item.endsTime)) {
    return [];
  }
  let periods = weeklyPeriods;
  const custom = exceptions.find(
    (item) => item.type === 'custom_hours' && item.startsTime && item.endsTime,
  );
  if (custom?.startsTime && custom.endsTime) {
    periods = [{ start: custom.startsTime, end: custom.endsTime }];
  }
  for (const item of exceptions) {
    if (!isBlockingMemberException(item.type) || !item.startsTime || !item.endsTime) continue;
    const cutStart = hhmmToMinutes(item.startsTime);
    const cutEnd = hhmmToMinutes(item.endsTime);
    if (cutEnd <= cutStart) continue;
    const next: HourPeriod[] = [];
    for (const period of periods) {
      const periodStart = hhmmToMinutes(period.start);
      const periodEnd = hhmmToMinutes(period.end);
      if (cutEnd <= periodStart || cutStart >= periodEnd) {
        next.push(period);
        continue;
      }
      if (periodStart < cutStart) next.push({ start: period.start, end: item.startsTime });
      if (cutEnd < periodEnd) next.push({ start: item.endsTime, end: period.end });
    }
    periods = next;
  }
  return periods;
}

/**
 * Canonical practitioner availability for one civil date.
 *
 * Source of truth is the practitioner calendar: weekly working hours, then
 * that practitioner's date exceptions (custom hours, vacation, unavailable).
 * Weekly clinic opening hours are informational and do not clip or enable
 * these windows. A genuine clinic-wide closed exception still blocks everyone.
 */
export function getEffectiveBookableWindows(input: {
  clinicPeriods?: HourPeriod[] | null;
  clinicSource?: ClinicHoursSource;
  clinicExceptionType?: ClinicHoursExceptionType | null;
  clinicianDay?: ClinicianDayHours | null;
  memberExceptions?: readonly MemberDayException[] | null;
}): HourPeriod[] {
  if (input.clinicSource === 'exception' && input.clinicExceptionType === 'closed') {
    return [];
  }
  const weekly =
    input.clinicianDay?.enabled && input.clinicianDay.start && input.clinicianDay.end
      ? [{ start: input.clinicianDay.start, end: input.clinicianDay.end }]
      : [];
  return applyMemberDayExceptions(weekly, input.memberExceptions ?? []);
}

export function bookableClinicClinicianWindows(
  clinicPeriods: HourPeriod[] | null | undefined,
  clinicianDay: ClinicianDayHours | null | undefined,
  clinicSource: ClinicHoursSource = 'weekly',
): HourPeriod[] {
  return getEffectiveBookableWindows({
    clinicPeriods,
    clinicSource,
    clinicianDay,
  });
}

/** Slot starts that fit entirely inside the windows. Interval is caller-supplied. */
export function slotStartMinutesWithinWindows(
  windows: HourPeriod[],
  durationMinutes: number,
  intervalMinutes: number,
): number[] {
  if (durationMinutes <= 0 || intervalMinutes <= 0) return [];
  const starts: number[] = [];
  for (const period of windows) {
    let slotStartMin = hhmmToMinutes(period.start);
    const windowEnd = hhmmToMinutes(period.end);
    while (slotStartMin + durationMinutes <= windowEnd) {
      starts.push(slotStartMin);
      slotStartMin += intervalMinutes;
    }
  }
  return starts;
}

/** Clinic civil YYYY-MM-DD values from today through `advanceDays` (inclusive). */
export function listClinicCivilDates(advanceDays: number, now = new Date()): string[] {
  const start = startOfZonedDay(now);
  const dates: string[] = [];
  for (let i = 0; i <= advanceDays; i++) {
    dates.push(zonedYmd(addZonedDays(start, i)));
  }
  return dates;
}

/**
 * Dates the public/portal booking picker may offer.
 * Uses Europe/Copenhagen civil dates so midnight/DST does not shift the exception.
 */
export const EN_WEEKDAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

export type EnWeekdayKey = (typeof EN_WEEKDAY_KEYS)[number];

export function enWeekdayKeyFromCivilDate(isoDate: string): EnWeekdayKey {
  return EN_WEEKDAY_KEYS[parseCivilDate(isoDate).getDay()];
}

export interface PractitionerScheduleForDates {
  workingHours?: Partial<Record<EnWeekdayKey, ClinicianDayHours | null>> | null;
  exceptions?: Array<{
    type: MemberExceptionType;
    startsOn: string;
    endsOn: string;
    startsTime?: string | null;
    endsTime?: string | null;
  }>;
}

/**
 * Dates that have at least one practitioner calendar window.
 * Does not use weekly clinic opening hours. Clinic-wide closed exceptions
 * still hide the date.
 */
export function listBookablePractitionerDates(input: {
  practitioners: PractitionerScheduleForDates[];
  clinicExceptions?: Map<string, ClinicHoursException> | ClinicHoursException[];
  advanceDays: number;
  now?: Date;
  limit?: number;
}): Date[] {
  const clinicExceptions =
    input.clinicExceptions instanceof Map
      ? input.clinicExceptions
      : exceptionByDate(input.clinicExceptions);
  const hasSchedule = input.practitioners.some(
    (practitioner) =>
      (practitioner.exceptions?.length ?? 0) > 0 ||
      Object.values(practitioner.workingHours ?? {}).some((day) => day?.enabled),
  );
  const dates: Date[] = [];
  const limit = input.limit ?? 90;
  for (const iso of listClinicCivilDates(input.advanceDays, input.now)) {
    const clinicException = clinicExceptions.get(iso) ?? null;
    if (clinicException?.exception_type === 'closed') continue;
    if (!hasSchedule) {
      dates.push(parseCivilDate(iso));
      if (dates.length >= limit) break;
      continue;
    }
    const dayKey = enWeekdayKeyFromCivilDate(iso);
    const anyOpen = input.practitioners.some((practitioner) => {
      const covering = (practitioner.exceptions ?? []).filter(
        (row) => row.startsOn <= iso && row.endsOn >= iso,
      );
      return (
        getEffectiveBookableWindows({
          clinicSource: clinicException ? 'exception' : 'weekly',
          clinicExceptionType: clinicException?.exception_type ?? null,
          clinicianDay: practitioner.workingHours?.[dayKey],
          memberExceptions: covering.map((row) => ({
            type: row.type,
            startsTime: row.startsTime,
            endsTime: row.endsTime,
          })),
        }).length > 0
      );
    });
    if (!anyOpen) continue;
    dates.push(parseCivilDate(iso));
    if (dates.length >= limit) break;
  }
  return dates;
}

export function catalogClinicianToSchedule(clinician: {
  working_hours?: Partial<Record<EnWeekdayKey, ClinicianDayHours | null>> | null;
  schedule_exceptions?: Array<{
    exception_type: MemberExceptionType;
    starts_on: string;
    ends_on: string;
    starts_time?: string | null;
    ends_time?: string | null;
  }>;
}): PractitionerScheduleForDates {
  return {
    workingHours: clinician.working_hours ?? undefined,
    exceptions: (clinician.schedule_exceptions ?? []).map((row) => ({
      type: row.exception_type,
      startsOn: row.starts_on,
      endsOn: row.ends_on,
      startsTime: row.starts_time,
      endsTime: row.ends_time,
    })),
  };
}

export function listBookableClinicDates(input: {
  weeklyHours: WeeklyHoursShape;
  exceptions?: Map<string, ClinicHoursException> | ClinicHoursException[];
  advanceDays: number;
  now?: Date;
  limit?: number;
}): Date[] {
  const exceptions =
    input.exceptions instanceof Map ? input.exceptions : exceptionByDate(input.exceptions);
  const dates: Date[] = [];
  const limit = input.limit ?? 90;
  for (const iso of listClinicCivilDates(input.advanceDays, input.now)) {
    const resolved = resolveClinicHoursForCivilDate({
      isoDate: iso,
      weeklyHours: input.weeklyHours,
      exception: exceptions.get(iso) ?? null,
    });
    if (!isCivilDateOpen(resolved)) continue;
    dates.push(parseCivilDate(iso));
    if (dates.length >= limit) break;
  }
  return dates;
}
